import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router';
import { isApiError } from '@/api/errors';
import { Badge } from '@/components/Badge';
import { Button, buttonClasses } from '@/components/Button';
import { Input } from '@/components/Input';
import { Sheet } from '@/components/Sheet';
import { useToast } from '@/components/Toast';
import { cn } from '@/lib/cn';
import { QuantityStepper } from '@/features/customer/parts';
import type { FloorLocation, MenuItem, ModifierGroup } from './api';
import { Amount, Notice } from './parts';
import {
  useAddIntent,
  useAddToCart,
  useIntentKey,
  useOpenSession,
  useTransferSession,
} from './queries';
import { describeStaffError, locationText } from './wording';

function groupRule(group: ModifierGroup): string {
  const { min_selection: min, max_selection: max } = group;
  if (min === 0) return max === 1 ? 'Optional · choose up to 1' : `Optional · choose up to ${max}`;
  if (min === max) return `Required · choose ${min}`;
  return `Required · choose ${min} to ${max}`;
}

// ─── Add to the customer's cart ──────────────────────────────────────────────

/**
 * Item detail for the waiter. The only action is "Add to customer's cart": the customer places their own
 * order. The server confirms every rule and sets every price; no price is shown or sent.
 */
export function ItemSheet({
  item,
  sessionId,
  cartRevision,
  who,
  reviewHref,
  onClose,
}: {
  item: MenuItem;
  sessionId: string;
  /**
   * The revision of the cart when the waiter picked this item. It is kept for as long as the sheet is open:
   * if the customer changes the cart meanwhile, the add is refused (never merged) and the waiter reviews it.
   */
  cartRevision: number;
  /** "Table 3 (Rahul)": whose cart this is. */
  who: string;
  reviewHref: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const add = useAddToCart();
  const intent = useAddIntent();
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [chosen, setChosen] = useState<Record<string, string[]>>({});
  const [conflicted, setConflicted] = useState(false);
  const [openedAtRevision] = useState(cartRevision);

  const missing = item.modifier_groups.filter(
    (group) => (chosen[group.id]?.length ?? 0) < group.min_selection,
  );

  function toggle(group: ModifierGroup, optionId: string) {
    setChosen((current) => {
      const picked = current[group.id] ?? [];
      if (picked.includes(optionId)) {
        return { ...current, [group.id]: picked.filter((id) => id !== optionId) };
      }
      if (group.max_selection === 1) return { ...current, [group.id]: [optionId] };
      if (picked.length >= group.max_selection) return current;
      return { ...current, [group.id]: [...picked, optionId] };
    });
  }

  function submit() {
    if (add.isPending || conflicted || missing.length > 0 || !item.is_available) return;
    const modifiers = Object.values(chosen)
      .flat()
      .map((id) => ({ modifier_option_id: id }));
    const trimmed = notes.trim();
    const signature = JSON.stringify({
      sessionId,
      item: item.id,
      quantity,
      modifiers: modifiers.map((m) => m.modifier_option_id).sort(),
      notes: trimmed,
    });
    const { key, revision } = intent.intentFor(signature, openedAtRevision);
    add.mutate(
      {
        sessionId,
        idempotencyKey: key,
        input: {
          menu_item_id: item.id,
          quantity,
          modifiers,
          ...(trimmed ? { notes: trimmed } : {}),
          expected_revision: revision,
        },
      },
      {
        onSuccess: () => {
          intent.reset();
          toast.show(`${item.name} added to the customer's cart.`, { tone: 'success' });
          onClose();
        },
        onError: (error) => {
          // The backend answered: this intent is over. An uncertain failure (no answer) keeps it, so a
          // second press replays the first request instead of adding another line.
          if (isApiError(error) && !error.isNetworkError) intent.reset();
          if (isApiError(error) && error.hasCode('CART_REVISION_CONFLICT')) setConflicted(true);
        },
      },
    );
  }

  const failure = add.isError ? describeStaffError(add.error, 'add') : null;

  return (
    <Sheet open onClose={onClose} title={item.name}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="sr-only">Price</span>
            <Amount minor={item.base_price} className="text-xl font-bold text-ink tabular-nums" />
            {!item.is_available && <Badge tone="neutral">Unavailable</Badge>}
          </div>
          {item.description && (
            <p className="text-sm leading-snug text-muted">{item.description}</p>
          )}
          <p className="text-sm text-muted">
            Adds to the cart for <strong className="text-ink">{who}</strong>.
          </p>
        </div>

        {item.modifier_groups.map((group) => {
          const picked = chosen[group.id] ?? [];
          const single = group.min_selection === 1 && group.max_selection === 1;
          const full = group.max_selection > 1 && picked.length >= group.max_selection;
          return (
            <fieldset key={group.id} className="flex flex-col gap-2">
              <legend className="mb-2 text-sm font-semibold text-ink">
                {group.name} <span className="font-normal text-muted">· {groupRule(group)}</span>
              </legend>
              {group.options.map((option) => {
                const checked = picked.includes(option.id);
                const blocked = (!checked && full) || conflicted;
                const priced = option.price_delta !== 0;
                const priceId = `staff-option-price-${option.id}`;
                return (
                  // The price sits beside the label, not inside it: the control keeps the option's own name
                  // and the price is read out as its description.
                  <div
                    key={option.id}
                    className={cn(
                      'flex min-h-12 items-center gap-3 rounded-control border px-3',
                      checked ? 'border-primary bg-primary-soft' : 'border-line bg-white',
                      blocked && 'text-faint',
                    )}
                  >
                    <label className="flex min-w-0 flex-1 items-center gap-3 text-base text-ink">
                      <input
                        type={single ? 'radio' : 'checkbox'}
                        name={`group-${group.id}`}
                        className="size-5 shrink-0 accent-primary"
                        checked={checked}
                        disabled={blocked}
                        aria-describedby={priced ? priceId : undefined}
                        onChange={() => toggle(group, option.id)}
                      />
                      <span className="min-w-0 break-words">{option.name}</span>
                    </label>
                    {priced && (
                      <span
                        id={priceId}
                        className="shrink-0 text-sm font-medium text-muted tabular-nums"
                      >
                        {option.price_delta > 0 ? '+' : '−'}
                        <Amount minor={Math.abs(option.price_delta)} />
                      </span>
                    )}
                  </div>
                );
              })}
            </fieldset>
          );
        })}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="staff-item-notes" className="text-sm font-medium text-ink">
            Note for the kitchen <span className="font-normal text-muted">(optional)</span>
          </label>
          <textarea
            id="staff-item-notes"
            rows={2}
            maxLength={255}
            value={notes}
            disabled={conflicted}
            onChange={(event) => setNotes(event.target.value)}
            className="w-full rounded-control border border-field bg-white px-3.5 py-2 text-base text-ink"
          />
        </div>

        {failure && <Notice tone={conflicted ? 'warn' : 'error'}>{failure}</Notice>}
        {missing.length > 0 && !conflicted && (
          <p id="staff-item-missing" className="text-sm text-muted">
            Choose {missing.map((group) => group.name).join(', ')} to continue.
          </p>
        )}

        {/* The quantity and the one action stay in reach at the foot of the sheet, like the customer's. */}
        <div className="sticky bottom-0 -mx-5 -mb-5 flex items-center gap-3 border-t border-line bg-white px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {conflicted ? (
            <Link
              to={reviewHref}
              className={buttonClasses({ variant: 'primary', size: 'lg', fullWidth: true })}
            >
              Review the cart
            </Link>
          ) : (
            <>
              <QuantityStepper
                value={quantity}
                label={item.name}
                onChange={setQuantity}
                disabled={add.isPending}
              />
              <Button
                variant="primary"
                size="lg"
                className="flex-1"
                loading={add.isPending}
                disabled={missing.length > 0 || !item.is_available}
                aria-describedby={missing.length > 0 ? 'staff-item-missing' : undefined}
                onClick={submit}
              >
                Add to customer&apos;s cart
              </Button>
            </>
          )}
        </div>
      </div>
    </Sheet>
  );
}

// ─── Open a session ──────────────────────────────────────────────────────────

export function OpenSessionSheet({
  location,
  onOpened,
  onClose,
}: {
  location: FloorLocation;
  onOpened: (sessionId: string) => void;
  onClose: () => void;
}) {
  const open = useOpenSession();
  const intent = useIntentKey();
  const isSeat = location.location_type === 'SEAT';
  const [name, setName] = useState('');
  const [guests, setGuests] = useState('1');
  const [attempted, setAttempted] = useState(false);
  const where = locationText(location.location_type, location.location_label);

  const size = isSeat ? 1 : Number(guests);
  const nameError = attempted && name.trim() === '' ? 'Enter a name.' : undefined;
  const guestsError =
    attempted && !isSeat && !(Number.isInteger(size) && size >= 1)
      ? 'Enter the number of guests.'
      : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    const display = name.trim();
    if (display === '' || !(Number.isInteger(size) && size >= 1) || open.isPending) return;
    const input = { location_id: location.location_id, display_name: display, party_size: size };
    // The key is kept while the inputs are the same: a repeat after a lost answer replays, never opens twice.
    open.mutate(
      { input, idempotencyKey: intent.keyFor(JSON.stringify(input)) },
      {
        onSuccess: (result) => {
          intent.reset();
          onOpened(result.session_id);
        },
        onError: (error) => {
          if (isApiError(error) && !error.isNetworkError) intent.reset();
        },
      },
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={isSeat ? `Seat a viewer at ${where}` : `Seat a party at ${where}`}
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input
          label={isSeat ? 'Viewer name' : 'Guest name'}
          value={name}
          maxLength={100}
          onChange={(event) => setName(event.target.value)}
          error={nameError}
          autoComplete="off"
        />
        {!isSeat && (
          <Input
            label="Number of guests"
            type="number"
            inputMode="numeric"
            min={1}
            value={guests}
            onChange={(event) => setGuests(event.target.value)}
            error={guestsError}
          />
        )}
        {open.error && (
          <p role="alert" className="text-sm font-medium text-danger">
            {describeStaffError(open.error, 'open')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={open.isPending}>
            Open session
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

// ─── Move a session ──────────────────────────────────────────────────────────

/**
 * Moves the session (with its cart, orders and bill) to another table or seat. Two steps: choose, then
 * confirm in a sentence that names both places. The backend decides whether the move is allowed.
 */
export function TransferSheet({
  sessionId,
  fromLocationId,
  from,
  party,
  locations,
  onMoved,
  onClose,
}: {
  sessionId: string;
  fromLocationId: string;
  from: string;
  party: string;
  locations: FloorLocation[];
  onMoved: () => void;
  onClose: () => void;
}) {
  const transfer = useTransferSession();
  const [targetId, setTargetId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const targets = useMemo(
    () => locations.filter((l) => l.location_id !== fromLocationId),
    [locations, fromLocationId],
  );
  const zones = useMemo(() => {
    const byZone = new Map<string, FloorLocation[]>();
    for (const location of targets) {
      const zone = location.zone_name ?? 'Other';
      byZone.set(zone, [...(byZone.get(zone) ?? []), location]);
    }
    return [...byZone.entries()];
  }, [targets]);
  const target = targets.find((l) => l.location_id === targetId) ?? null;
  const to = target ? locationText(target.location_type, target.location_label) : '';
  /** "In use · seats 4": what the waiter needs to judge a table before confirming, from the floor data. */
  const candidateText = (location: FloorLocation) => {
    const state = location.active_sessions.length > 0 ? 'In use' : 'Vacant';
    return location.location_type === 'SEAT'
      ? state
      : `${state} · seats ${location.nominal_capacity}`;
  };

  function confirm() {
    if (!target || transfer.isPending) return;
    const trimmed = reason.trim();
    transfer.mutate(
      {
        fromLocationId,
        input: {
          session_id: sessionId,
          to_location_id: target.location_id,
          ...(trimmed ? { reason: trimmed } : {}),
        },
      },
      { onSuccess: onMoved },
    );
  }

  return (
    <Sheet open onClose={onClose} title="Move party">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          <strong className="text-ink">{party}</strong>, now at{' '}
          <strong className="text-ink">{from}</strong>.
        </p>

        {!confirming && (
          <>
            {targets.length === 0 && (
              <p className="text-sm text-muted">There is nowhere else to move to.</p>
            )}
            {zones.map(([zone, items]) => (
              <fieldset key={zone} className="flex flex-col gap-1">
                <legend className="text-sm font-semibold text-muted">{zone}</legend>
                {items.map((location) => {
                  const busy = location.active_sessions.length > 0;
                  const label = locationText(location.location_type, location.location_label);
                  return (
                    <label
                      key={location.location_id}
                      className="flex min-h-11 items-center justify-between gap-3 text-base text-ink"
                    >
                      <span className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="move-to"
                          className="size-5 accent-primary"
                          checked={targetId === location.location_id}
                          onChange={() => setTargetId(location.location_id)}
                        />
                        {label}
                      </span>
                      <Badge tone={busy ? 'neutral' : 'active'}>{candidateText(location)}</Badge>
                    </label>
                  );
                })}
              </fieldset>
            ))}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" disabled={!target} onClick={() => setConfirming(true)}>
                Continue
              </Button>
            </div>
          </>
        )}

        {confirming && target && (
          <>
            <p className="text-base text-ink">
              Move <strong>{party}</strong> from <strong>{from}</strong> to <strong>{to}</strong>?
              Their cart and orders move with them.
            </p>
            <Input
              label="Reason (optional)"
              value={reason}
              maxLength={255}
              onChange={(event) => setReason(event.target.value)}
              autoComplete="off"
            />
            {transfer.error && (
              <Notice tone="error">{describeStaffError(transfer.error, 'transfer')}</Notice>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="secondary"
                disabled={transfer.isPending}
                onClick={() => {
                  transfer.reset();
                  setConfirming(false);
                }}
              >
                Back
              </Button>
              <Button variant="primary" loading={transfer.isPending} onClick={confirm}>
                Move to {to}
              </Button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
